import { memo, useContext, type JSX } from 'react';

import { ServiceCode } from '@/shared/types';
import { Button, ButtonVariant, ConditionView, Modal, Typography } from '@/shared/ui';

import { BlockTitle } from '@/components/old';
import ButtonOld from '@/components/old/Button';

import { redirectToInternalServices } from '@/helpers/route';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import ModalContext from '../../../contexts/Modal/ModalContext';
import { ModalBorderTopColor, ModalConstructor } from '../ModalConstructor';

import './ServiceDraftExistingAlert.scss';

interface BaseProps {
  variant?: 'new' | 'legacy';
  startNewDraft: () => void;
  continueDraft: () => void;
}

interface Props extends BaseProps {
  variant?: 'new';
  serviceCode: ServiceCode;
  renderDescription?: never;
  customMessage?: string;
  customCloseDraftModalHandler?: () => void;
}

interface LegacyProps extends BaseProps {
  variant?: 'legacy';
  serviceCode?: never;
  renderDescription?: () => JSX.Element;
  customMessage?: never;
  customCloseDraftModalHandler?: never;
}

export type ServiceDraftExistingAlertProps = LegacyProps | Props;

export const ServiceDraftExistingAlert = memo<ServiceDraftExistingAlertProps>(function ({
  variant = 'legacy',
  serviceCode,
  startNewDraft,
  continueDraft,
  renderDescription,
  customMessage,
  customCloseDraftModalHandler,
}) {
  const { isModalOpen, toggleModal } = useContext(ModalContext);
  const { getServiceByCode } = useContext(ServicesContext);

  const handleClose = (): void => {
    toggleModal(false);
    customCloseDraftModalHandler ? customCloseDraftModalHandler() : redirectToInternalServices();
  };

  return (
    <ConditionView
      if={variant === 'legacy'}
      else={
        <Modal
          closable
          maskClosable={false}
          open={isModalOpen}
          title="Для цієї послуги вже створена чернетка"
          classNames={{
            footer: `service-exist-alert__buttons-box service-exist-alert__buttons-box--${variant}`,
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  continueDraft();
                  toggleModal(false);
                }}
              >
                Продовжити чернетку
              </Button>
              <Button
                variant={ButtonVariant.SECONDARY}
                onClick={() => {
                  startNewDraft();
                  toggleModal(false);
                }}
              >
                Створити послугу заново
              </Button>
            </>
          }
          onCancel={handleClose}
        >
          {customMessage ? (
            <Typography color="graphite">{customMessage}</Typography>
          ) : (
            <Typography color="graphite">
              Для послуги &quot;{getServiceByCode(serviceCode!)?.name}&quot; вже створена чернетка.
              <br />
              Ви можете продовжити заповнення чернетки, або почати заповнення послуги спочатку (чернетку в такому
              випадку буде видалено).
            </Typography>
          )}
        </Modal>
      }
    >
      <ModalConstructor modalBorderTopColor={ModalBorderTopColor.blue} onCancel={handleClose}>
        <div className="service-exist-alert">
          <div className="service-exist-alert__title">
            <BlockTitle size="md" withoutBaseStyles>
              Заява на дану послугу вже знаходиться у чернетці
            </BlockTitle>
            <span>
              {typeof renderDescription === 'function'
                ? renderDescription()
                : 'Бажаєте продовжити заповнення, чи почати заново?'}
            </span>
          </div>

          <div className={`service-exist-alert__buttons-box service-exist-alert__buttons-box--${variant}`}>
            <ButtonOld
              isBorder={true}
              isMaxWidth={true}
              onClick={(): void => {
                startNewDraft();
                toggleModal(false);
              }}
            >
              Почати заново
            </ButtonOld>
            <ButtonOld
              isMaxWidth={true}
              onClick={(): void => {
                continueDraft();
                toggleModal(false);
              }}
            >
              Продовжити
            </ButtonOld>
          </div>
        </div>
      </ModalConstructor>
    </ConditionView>
  );
});
